import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import {useI18n} from '@/i18n'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {CrossCuttingLifecycleEventsControllerListEndpoint} from '@/types/endpoint-contracts'
import {PageLayout} from '@/components/shared/PageLayout'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Pagination} from '@/components/ui/pagination'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {ErrorBanner} from '@/components/ui/error-banner'
import {EmptyState} from '@/components/shared/EmptyState'
import {ClearFiltersButton} from '@/components/shared/ClearFiltersButton'
import {TextLink} from '@/components/shared/TextLink'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {SortableHeader} from '@/components/ui/sortable-header'
import {CONCEPT_ICON} from '@/lib/concepts'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {LIFECYCLE_EVENTS_QUERY_KEY} from '@/hooks/use-lifecycle-events'
import {LifecycleEntityType, type LifecycleEvent} from '@/types/lifecycle-event'
import {TriggerSourceBadge} from '@/types/trigger-source-badge'

const REASON_TRUNCATE_LIMIT = 60

function truncateReason(reason: string): string {
    if (reason.length <= REASON_TRUNCATE_LIMIT) {
        return reason
    }
    return reason.slice(0, REASON_TRUNCATE_LIMIT - 1) + '…'
}

function entityLinkFor(event: LifecycleEvent): string | null {
    if (event.entityType === LifecycleEntityType.IDENTITY) {
        return routes.identityManagement.identities.detail(event.entityId)
    }
    return null
}

interface LifecycleEventsProps {
    embedded?: boolean
}

/**
 * Cross-cutting lifecycle events ops dashboard (spec §8.4 / issue 594.07).
 *
 * <p>Renders a filterable, paginated, server-sorted timeline of identity and
 * account state changes. Permission gate is {@code analytics:view} (auditor +
 * admin). The page is accessible via {@code /admin/lifecycle-events}.
 */
export function LifecycleEvents({embedded}: LifecycleEventsProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.lifecycleEvents.dashboard.pageTitle)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
        sortBy, sortDir, setSort,
    } = useServerPagination({
        endpoint: CrossCuttingLifecycleEventsControllerListEndpoint,
        sortable: true,
        defaultSortBy: CrossCuttingLifecycleEventsControllerListEndpoint.sort.CHANGED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const {data, isLoading, error} = useQuery({
        queryKey: [LIFECYCLE_EVENTS_QUERY_KEY, queryString, page, pageSize],
        queryFn: () => services.analytics.lifecycleEvents(
            Object.fromEntries(new URLSearchParams(queryString))),
    })

    const events: LifecycleEvent[] = data?.content ?? []
    const totalItems = data?.totalElements ?? 0
    const totalPages = data?.totalPages ?? 0

    const hasFilters = useMemo(() => {
        return Object.keys(searchAndFilters.filters).length > 0
            || (searchAndFilters.q?.trim().length ?? 0) > 0
    }, [searchAndFilters])

    const renderEntityCell = (event: LifecycleEvent) => {
        const variant = event.entityType === LifecycleEntityType.IDENTITY
            ? BadgeVariant.SECONDARY
            : BadgeVariant.OUTLINE
        const label = event.entityType === LifecycleEntityType.IDENTITY
            ? i18n.lifecycleEvents.dashboard.entityType.IDENTITY
            : i18n.lifecycleEvents.dashboard.entityType.ACCOUNT
        const detailLink = entityLinkFor(event)
        return (
            <InlineRow gap="sm" align="center">
                <Badge variant={variant} data-testid={`badge-entity-${event.entityType}`}>
                    {label}
                </Badge>
                {detailLink ? (
                    <TextLink
                        to={detailLink}
                        testId={`entity-link-${event.id}`}
                        stopPropagation
                    >
                        {event.entityId}
                    </TextLink>
                ) : (
                    <MutedText>{event.entityId}</MutedText>
                )}
            </InlineRow>
        )
    }

    const renderFromToCell = (event: LifecycleEvent) => {
        if (event.fromState === null) {
            return (
                <Badge variant={BadgeVariant.OUTLINE}>
                    {i18n.lifecycleEvents.dashboard.createdSeparator({state: event.toState})}
                </Badge>
            )
        }
        return (
            <MutedText size="xs">
                {i18n.lifecycleEvents.dashboard.fromToSeparator({from: event.fromState, to: event.toState})}
            </MutedText>
        )
    }

    const renderActorCell = (event: LifecycleEvent) => {
        if (event.actor) {
            return <span>{event.actor.displayName}</span>
        }
        const label = i18n.lifecycleEvents.dashboard.actorSystemWithSource({source: event.triggerSource})
        return (
            <Badge variant={BadgeVariant.OUTLINE} data-testid={`actor-system-${event.id}`}>
                {label}
            </Badge>
        )
    }

    const renderReasonCell = (event: LifecycleEvent) => {
        if (!event.reason) {
            return <MutedText>{i18n.lifecycleEvents.dashboard.none}</MutedText>
        }
        return (
            <span title={event.reason}>{truncateReason(event.reason)}</span>
        )
    }

    const renderClearFilters = () => (
        <ClearFiltersButton
            hasActiveFilters
            onClick={() => setSearchAndFilters({q: '', filters: {}, dynamicAttributes: {}})}
        />
    )

    return (
        <>
            <ErrorBanner error={error}/>
            <PageLayout
                variant="list"
                embedded={embedded}
                icon={CONCEPT_ICON.lifecycleEvents}
                title={i18n.lifecycleEvents.dashboard.title}
                description={i18n.lifecycleEvents.dashboard.menuDescription}
                count={totalItems}
                data-testid="lifecycle-events-list"
                filters={
                    <UnifiedSearchBar
                        contract={CrossCuttingLifecycleEventsControllerListEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.lifecycleEvents.dashboard.title}
                    />
                }
                emptyState={!isLoading && events.length === 0 ? (
                    <EmptyState
                        title={i18n.lifecycleEvents.empty.noResultsForFilters}
                        description={hasFilters ? undefined : i18n.lifecycleEvents.empty.cutoverNote}
                        action={hasFilters ? renderClearFilters() : undefined}
                        data-testid="lifecycle-events-empty"
                    />
                ) : undefined}
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                {isLoading ? (
                    <MutedText block centered data-testid="lifecycle-events-loading">
                        {i18n.lifecycleEvents.dashboard.loading}
                    </MutedText>
                ) : events.length > 0 ? (
                    <Table data-help-prefix="lifecycleEventsOps">
                        <TableHeader>
                            {/* read-only */}
                            <TableRow>
                                <TableHead data-help-key="changedAt" data-testid="col-changed-at">
                                    <SortableHeader
                                        label={i18n.lifecycleEvents.dashboard.columns.changedAt}
                                        sortKey={CrossCuttingLifecycleEventsControllerListEndpoint.sort.CHANGED_AT}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-help-key="entity" data-testid="col-entity">
                                    {i18n.lifecycleEvents.dashboard.columns.entity}
                                </TableHead>
                                <TableHead data-help-key="fromTo" data-testid="col-from-to">
                                    {i18n.lifecycleEvents.dashboard.columns.fromTo}
                                </TableHead>
                                <TableHead data-help-key="triggerSource" data-testid="col-trigger-source">
                                    <SortableHeader
                                        label={i18n.lifecycleEvents.dashboard.columns.triggerSource}
                                        sortKey={CrossCuttingLifecycleEventsControllerListEndpoint.sort.TRIGGER_SOURCE}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-help-key="actor" data-testid="col-actor">
                                    {i18n.lifecycleEvents.dashboard.columns.actor}
                                </TableHead>
                                <TableHead data-help-key="reason" data-testid="col-reason">
                                    {i18n.lifecycleEvents.dashboard.columns.reason}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {events.map((event) => {
                                const triggerBadge = TriggerSourceBadge.of(event.triggerSource)
                                return (
                                    <TableRow
                                        key={event.id}
                                        data-readonly
                                        data-testid={`lifecycle-event-row-${event.id}`}
                                    >
                                        <TableCell>{formatTimestamp(event.changedAt)}</TableCell>
                                        <TableCell>{renderEntityCell(event)}</TableCell>
                                        <TableCell>{renderFromToCell(event)}</TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={triggerBadge.variant}
                                                data-testid={`trigger-source-${event.id}`}
                                            >
                                                {event.triggerSource}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{renderActorCell(event)}</TableCell>
                                        <TableCell data-testid={`reason-cell-${event.id}`}>
                                            {renderReasonCell(event)}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                ) : null}
            </PageLayout>
        </>
    )
}
